import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Users, BarChart3, Edit, Trophy, TrendingUp, LogOut, ArrowLeft, Swords, FileDown } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import ProfileSettings from './ProfileSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ userId, isOwnProfile = false, onLogout, onBack, isDark }) => {
  const { user, token, logout } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [showEditDialog, setShowEditDialog] = useState(false);

  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (targetUserId) {
      fetchProfile();
      fetchPosts();
      fetchStats();
    }
  }, [targetUserId]);

  const fetchProfile = async () => {
    try {
      if (isOwnProfile) {
        setProfileUser(user);
      } else {
        const response = await axios.get(`${API}/auth/user/${targetUserId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfileUser(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts/user/${targetUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/matches/stats/${targetUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Post deleted' });
      fetchPosts();
    } catch (error) {
      toast({ title: 'Failed to delete post', variant: 'destructive' });
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`${API}/export/stats-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `badminton-stats-${displayUser?.username || 'report'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: 'PDF downloaded!' });
    } catch (error) {
      toast({ title: 'Failed to export PDF', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const displayUser = profileUser || user;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      {/* Back button for viewing other profiles */}
      {!isOwnProfile && onBack && (
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-2" data-testid="profile-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Friends
        </Button>
      )}

      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          {/* Banner */}
          <div 
            className="h-24 -mx-6 -mt-6 rounded-t-lg mb-4"
            style={{ backgroundColor: displayUser?.banner_color || '#3b82f6' }}
          />
          
          {/* Profile Image & Edit Button */}
          <div className="flex justify-between items-start -mt-16 mb-4">
            <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
              <AvatarImage src={displayUser?.profile_image} />
              <AvatarFallback 
                className="text-3xl font-bold text-white"
                style={{ backgroundColor: displayUser?.banner_color || '#3b82f6' }}
              >
                {displayUser?.username?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            {isOwnProfile && (
              <div className="flex gap-2 mt-12">
                <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="edit-profile-btn">
                      <Edit className="h-4 w-4 mr-1" />
                      Edit Profile
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Edit Profile</DialogTitle>
                    </DialogHeader>
                    <ProfileSettings onSave={() => setShowEditDialog(false)} />
                  </DialogContent>
                </Dialog>
                {onLogout && (
                  <Button variant="outline" size="sm" onClick={onLogout} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="export-pdf-btn">
                  <FileDown className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              </div>
            )}
          </div>

          {/* Username & Info */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold">{displayUser?.username}</h1>
            <p className="text-gray-500 text-sm">{isOwnProfile ? displayUser?.email : ''}</p>
            {displayUser?.age_category && (
              <Badge variant="secondary" className="mt-1">{displayUser.age_category}</Badge>
            )}
          </div>

          {/* Bio */}
          {(displayUser?.bio || stats?.bio) && (
            <p className="text-gray-700 text-sm mb-4">{displayUser?.bio || stats?.bio}</p>
          )}

          {/* Stats Row */}
          <div className="flex gap-6 mb-4">
            <button className="text-center hover:opacity-70" onClick={() => setActiveTab('friends')}>
              <p className="text-xl font-bold">{displayUser?.friends?.length || 0}</p>
              <p className="text-xs text-gray-500">Friends</p>
            </button>
            <button className="text-center hover:opacity-70" onClick={() => setActiveTab('stats')}>
              <p className="text-xl font-bold">{stats?.total_matches || 0}</p>
              <p className="text-xs text-gray-500">Matches</p>
            </button>
            <button className="text-center hover:opacity-70" onClick={() => setActiveTab('stats')}>
              <p className="text-xl font-bold text-green-600">{stats?.win_rate || 0}%</p>
              <p className="text-xs text-gray-500">Win Rate</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Posts / Stats / Friends */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full">
          <TabsTrigger value="posts" className="flex-1">Posts</TabsTrigger>
          <TabsTrigger value="stats" className="flex-1">Stats</TabsTrigger>
          <TabsTrigger value="friends" className="flex-1">Friends</TabsTrigger>
        </TabsList>

        {/* Posts Tab */}
        <TabsContent value="posts" className="mt-4">
          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No posts yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1">
              {posts.map((post) => (
                <div key={post.id} className="relative aspect-square group">
                  {post.media_type === 'video' ? (
                    <video src={post.media_url} className="w-full h-full object-cover" />
                  ) : (
                    <img src={post.media_url} alt="" className="w-full h-full object-cover" />
                  )}
                  {isOwnProfile && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeletePost(post.id)}
                        data-testid={`delete-post-${post.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Trophy className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
                  <p className="text-3xl font-bold text-green-600">{stats?.wins || 0}</p>
                  <p className="text-sm text-gray-600">Wins</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <TrendingUp className="h-8 w-8 mx-auto text-red-500 mb-2" />
                  <p className="text-3xl font-bold text-red-600">{stats?.losses || 0}</p>
                  <p className="text-sm text-gray-600">Losses</p>
                </div>
              </div>
              <div className="mt-4 text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-4xl font-bold text-blue-600">{stats?.win_rate || 0}%</p>
                <p className="text-sm text-gray-600">Win Rate</p>
              </div>
              {stats?.current_streak > 0 && (
                <div className="mt-4 text-center">
                  <p className="text-lg">
                    Current Streak: <span className="font-bold">{stats.current_streak}</span>
                    {stats.streak_type === 'win' ? ' W' : ' L'}
                  </p>
                </div>
              )}
              {/* Top Opponents */}
              {stats?.top_opponents?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Swords className="h-4 w-4" /> Top Opponents
                  </h3>
                  <div className="space-y-2">
                    {stats.top_opponents.map((opp, i) => (
                      <div key={opp.name} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg" data-testid={`top-opponent-${i}`}>
                        <span className="text-sm font-medium">{opp.name}</span>
                        <div className="flex gap-2 text-xs">
                          <span className="text-green-600 font-semibold">{opp.wins}W</span>
                          <span className="text-red-600 font-semibold">{opp.losses}L</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Friends Tab */}
        <TabsContent value="friends" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {!displayUser?.friends || displayUser.friends.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No friends yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {displayUser.friends.map((friendId, index) => (
                    <div key={friendId} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                      <Avatar>
                        <AvatarFallback>F{index + 1}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">Friend</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Share Your Stats - only for own profile */}
      {isOwnProfile && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Share Your Stats</h3>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Get your embeddable stat card for forums & social media</p>
              </div>
              <a
                href={`${API}/embed/card/${displayUser?.username}/svg`}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0"
              >
                <img
                  src={`${API}/embed/card/${displayUser?.username}/svg`}
                  alt="Your stat card"
                  className="w-48 rounded-lg shadow-sm"
                  data-testid="profile-embed-preview"
                />
              </a>
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`[![${displayUser?.username} Stats](${API}/embed/card/${displayUser?.username}/svg)](https://mybaddyscores.com/leaderboard)`);
                  toast({ title: 'Copied!', description: 'Markdown embed code copied' });
                }}
                data-testid="copy-embed-markdown"
              >
                Copy Markdown
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${API}/embed/card/${displayUser?.username}/svg`);
                  toast({ title: 'Copied!', description: 'Direct image URL copied' });
                }}
                data-testid="copy-embed-url"
              >
                Copy Image URL
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfilePage;
